import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { updateConsent, trackPageView, parseStoredConsent, type ConsentPreferences } from "@/lib/analytics";
import { applyConsentToSdks } from "@/lib/consentedSdks";
import { isClassroomPath } from "@/lib/classroomRoutes";

function saveAndApply(prefs: ConsentPreferences) {
  localStorage.setItem("cookie-consent", JSON.stringify(prefs));
  updateConsent(prefs);
  // PageViewTracker only fires on route change, so a user who grants analytics
  // via the banner wouldn't register in GA until they navigated. Send the
  // current page_view immediately so the just-consented session shows up in
  // Realtime right away.
  if (prefs.analytics) trackPageView(window.location.pathname);
  // Boot Sentry / OneSignal now if the user just granted the matching consent (#171).
  applyConsentToSdks(prefs);
}

// Shared parser so the dialog's toggles reflect the same legacy-format
// interpretation that analytics.ts applies to GA.
function loadPrefs(): ConsentPreferences | null {
  return parseStoredConsent(localStorage.getItem("cookie-consent"));
}

const CookieConsent = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  // In a classroom (a live lesson or the practice room) the fixed banner
  // overlays the call controls and the chat composer, and its policy link is a
  // full page load that would end the call. Consent defaults to denied
  // (initConsentMode), so deferring the prompt until the user leaves the room
  // tracks nothing early. `visible` is left alone meanwhile and this component
  // stays mounted across pages, so the banner comes back by itself afterwards.
  const { pathname } = useLocation();
  const inClassroom = isClassroomPath(pathname);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const openPrefs = useCallback(() => {
    const saved = loadPrefs();
    if (saved) {
      setAnalytics(saved.analytics);
      setMarketing(saved.marketing);
    }
    setPrefsOpen(true);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("cookie-consent")) {
      setVisible(true);
    }

    // Allow other components (e.g. footer link) to open the preferences dialog
    const handler = () => openPrefs();
    window.addEventListener("open-cookie-prefs", handler);
    return () => window.removeEventListener("open-cookie-prefs", handler);
  }, [openPrefs]);

  const accept = (prefs: ConsentPreferences) => {
    saveAndApply(prefs);
    setVisible(false);
    setPrefsOpen(false);
  };

  return (
    <>
      {visible && !inClassroom && (
        <div className="fixed bottom-0 left-0 right-0 z-50 px-3 pt-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:p-4 bg-background border-t border-border shadow-soft animate-slide-up">
          <div className="container mx-auto max-w-4xl flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <p className="text-xs leading-snug sm:text-sm sm:leading-normal text-muted-foreground flex-1">
              {t("cookieConsent.message")}{" "}
              <a href="/cookies" className="underline">{t("cookieConsent.policyLink")}</a> {t("cookieConsent.forDetails")}
            </p>
            <div className="flex w-full sm:w-auto flex-wrap items-center justify-center gap-x-2 gap-y-1 sm:gap-y-2">
              <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={() => accept({ analytics: false, marketing: false })}>{t("cookieConsent.essentialOnly")}</Button>
              <Button size="sm" className="flex-1 sm:flex-none" onClick={() => accept({ analytics: true, marketing: true })}>{t("cookieConsent.acceptAll")}</Button>
              <button className="basis-full sm:basis-auto min-h-11 sm:min-h-0 py-1 sm:py-0 text-xs text-foreground hover:underline rounded-sm focus-ring" onClick={openPrefs}>{t("cookieConsent.managePreferences")}</button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("cookieConsent.prefsTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">{t("cookieConsent.essential")}</Label>
              <Switch checked disabled />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">{t("cookieConsent.analytics")}</Label>
              <Switch checked={analytics} onCheckedChange={setAnalytics} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">{t("cookieConsent.marketing")}</Label>
              <Switch checked={marketing} onCheckedChange={setMarketing} />
            </div>
            <Button className="w-full" onClick={() => accept({ analytics, marketing })}>{t("cookieConsent.savePreferences")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
